import Head from "next/head";
import TitleProductAlt from "components/sections/TitleProductAlt";
import Button from "components/UI/Button";
import useScrollTop from "../../hooks/useScrollTop";
import TableAlt from "../../components/sections/TableAlt";
import Link from "next/link";
import { track } from "../../utils/amplitude";
import Stats from "components/sections/Stats";
import Title from "components/UI/Title";
import MigrationGuide from "components/UI/MigrationGuide";
import Reviews from "components/WhyUs/Reviews";
import { PromoBanner } from "components/Home/Promo";
import useWindowWidth from "hooks/useWindowWidth";
import cn from "classnames";
import SupportStats from "components/UI/Stats";
import Banner from "components/Blog/Banner";
import CustomerTrust from "components/UI/CustomerTrust";

const titles = [
  {
    id: "1",
    description: "",
  },

  {
    id: "2",
    description: "Apphud",
  },

  {
    id: "3",
    description: "Adapty",
  },
];

const items = [
  {
    id: "1",
    description: "ARPU, ARPPU, ARPAS, Cumulative LTV \n data up to 999 days",
    col1Check: "check",
    col2Text: "Limited",
  },
  {
    id: "2",
    description: "Trial value event to optimize User \n Acquisition campaigns",
    col1Check: "check",
    col2Check: "close",
  },
  {
    id: "3",
    description: "Advanced data filtration system",
    col1Text: "25+ properties",
    col2Text: "Limited (10 properties)",
  },
  {
    id: "4",
    description: "Number of tracked Events ",
    col1Text: "30+ events",
    col2Text: "17 events",
  },
  {
    id: "5",
    description: "Lightweight open-source SDK",
    col1Check: "check",
    col2Check: "check",
  },
  {
    id: "6",
    description: "Real-time analytics",
    col1Check: "check",
    col2Check: "check",
  },
  {
    id: "7",
    description: "Automatic server-side receipt validation",
    col1Check: "check",
    col2Check: "check",
  },
   {
    id: "8",
    description: "LTV predictions",
    col1Check: "check",
    col2Check: "check",
  },
   {
    id: "9",
    description: "MRR/ARR Movement Charts",
    col1Check: "check",
    col2Check: "close",
  },
   {
    id: "10",
    description: "Xiaomi SDK",
    col1Check: "check",
    col2Check: "close",
  },
  {
    id: "11",
    description: "Web2App Solution",
    col1Check: "check",
    col2Check: "close",
  },
  {
    id: "12",
    description:
      "Automated push notifications \n with advanced audience segmentation",
    col1Check: "check",
    col2Check: "close",
  },
  {
    id: "13",
    description: "Pricing",
    col1Text: "Price starts from $49/mo",
    col2Text: "Price starts from $99/mo",
  },
  {
    id: "14",
    description: "Users satisfaction rate (based on G2 score)",
    col1Text: "4.9/5",
    col2Text: "4.7/5",
  },
];

const data = [
  {
    text: "99,9% revenue data accuracy compared to App Store Connect and Google Play",
    src: "/stats/time.svg",
  },
  {
    text: "Advanced revenue analytics: Cumulative LTV up to 999 days, Trial value, etc",
    src: "/stats/measure.webp",
  },
  {
    text: "Rules – automation tool to send users push notifications",
    src: "/stats/visual_constructor.svg",
  },
  {
    text: "30+ tracked in-app revenue events",
    src: "/stats/system_status.png",
  },
];

const stats = [
  {
    title: "8,42",
    description: "NPS score",
  },
  {
    title: "40 min",
    description: "Average response time",
  },
  {
    title: "5 hours",
    description: "Average time to close response",
  },
  {
    title: "~90%",
    description: "Closed conversations",
  },
];

const bannerData = {
  title:
    "How Apphud helped AEZAKMI Group double app revenue and increase the conversion rate to subscription by 61% with A/B experiments",
  image: "/images/aezakmi.png",
  readTime: "6 min to read",
  buttonText: "Learn more",
  buttonUrl: "/blog/experiments-case-study",
};

export default function compare() {
  useScrollTop();
  const width = useWindowWidth();

  return (
    <div className="wrapper">
      <Head>
        <title>The best alternative to Adapty</title>
        <meta
          name="description"
          content="Discover what makes Apphud different from Adapty: 99.9% revenue data accuracy, advanced revenue analytics, automated push notification tool, and more."
        />
        <link
          rel="canonical"
          href="https://apphud.com/compare-apphud-to-adapty"
        />
        <meta
          name="keywords"
          content="adapty alternative, adapty competitors"
        />
        <meta property="og:title" content="The best alternative to Adapty" />
        <meta
          property="og:description"
          content="Discover what makes Apphud different from Adapty: 99.9% revenue data accuracy, advanced revenue analytics, automated push notification tool, and more."
        />
        <meta
          property="twitter:title"
          content="The best alternative to Adapty"
        />
        <meta
          property="twitter:description"
          content="Discover what makes Apphud different from Adapty: 99.9% revenue data accuracy, advanced revenue analytics, automated push notification tool, and more."
        />
        <meta
          property="twitter:url"
          content="https://apphud.com/compare-apphud-to-adapty"
        />
      </Head>

      <TitleProductAlt
        title={"Why Apphud is your best choice over Adapty?"}
        text={
          "Looking for a reliable subscription infrastructure with the most \n accurate data to grow your app revenue? Compared to Adapty, \n Apphud has many more features and benefits to cover all your \n business needs."
        }
        actions={
          <>
            <Link href="/demo" target="_blank">
              <Button title="Book a demo" />
            </Link>
            <a
              href="https://app.apphud.com/sign_up"
              onClick={() =>
                track("sign_up_button_clicked", { block: "top_link" })
              }
              style={width < 767 ? { width: "100%" } : {}}
            >
              <Button title="Start for Free" transparent />
            </a>
          </>
        }
      />

      <Title tag="h2">What makes us different</Title>

      <Stats data={data} alt />

      <TableAlt titles={titles} items={items} />

      <SupportStats stats={stats} />

      <div
        className={width > 922 ? "container" : ""}
        style={{ margin: width > 922 ? "160px auto 80px auto" : "60px auto" }}
      >
        <Banner {...bannerData} alt />
      </div>

      <CustomerTrust />

      <MigrationGuide name="Adapty" />

      <Reviews />

      <div
        className={cn(width > 922 ? "container" : "")}
        style={{ marginTop: width > 922 ? 160 : 100 }}
      >
        <PromoBanner text="Let’s grow your app revenue together" />
      </div>
    </div>
  );
}
